import { cn } from '../../lib/cn';
import {
  formatHours,
  formatMoney,
  formatMonth,
  formatMonthShort,
  formatPercent,
} from '../../lib/format';
import { ProgressBar } from './ProgressBar';

/**
 * The ONE budget-burn renderer.
 *
 * It takes the server's metric object verbatim — `{basis, measure, used,
 * budget, pct, periodMonth, periodClosed, lifetime}` — so the denominator, the
 * unit, the period and the colour thresholds cannot drift between the agreements
 * list, the agreement header and the client's agreement cards. Every one of those
 * screens used to compute its own version and they disagreed.
 *
 * Two rules this component exists to enforce:
 *
 * 1. **A period-scoped bar ALWAYS names its period.** The compact variant used
 *    to drop it and print a bare "of $6,500/mo", so a *current-month* bar sat in
 *    the same row as *lifetime* revenue, cost and margin with nothing to say so
 *    — a green 27% next to a −178% margin. The month is now part of the
 *    caption in every variant, and a final month on a closed engagement is
 *    marked as such.
 * 2. **A period-scoped bar ALWAYS shows the lifetime figure beside it.** One
 *    month is not a verdict. `burn.lifetime` renders on the same colour ramp so
 *    the colour of the bar and the sign of the margin can never disagree
 *    unnoticed. In `full` it is a second line with its own denominator; in the
 *    `compact` table cell it rides the bar's own row as "life 78%" (the
 *    denominator moves into the tooltip), because a stacked second block cost
 *    every retainer and hosting row ~15px of height on the one screen where how
 *    many rows you can see is the feature.
 */

export interface BurnLifetimeLike {
  used: number;
  budget: number;
  /** ratio, 1 = 100% */
  pct: number;
  months: number;
}

export interface BudgetBurnLike {
  /** Unit of used/budget: HOURS → hours, AMOUNT/MONTHLY_AMOUNT → money. */
  basis: 'HOURS' | 'AMOUNT' | 'MONTHLY_AMOUNT';
  /** What consumes the budget. */
  measure: 'HOURS' | 'BILLABLE_VALUE' | 'COST';
  used: number;
  budget: number;
  /** ratio, 1 = 100% */
  pct: number;
  /** 'YYYY-MM' when the burn covers a single month. */
  periodMonth: string | null;
  /** true when periodMonth is the final month of a closed agreement, not the live one. */
  periodClosed?: boolean;
  /** All-time burn against the fee accrued to date. */
  lifetime?: BurnLifetimeLike | null;
}

const MEASURE_NOUN: Record<BudgetBurnLike['measure'], string> = {
  HOURS: 'tracked',
  BILLABLE_VALUE: 'billable',
  COST: 'cost',
};

const unitOf = (burn: BudgetBurnLike, v: number) =>
  burn.basis === 'HOURS' ? `${formatHours(v)} h` : formatMoney(v, { whole: true });

/**
 * True when the bar's period is the month we are living through, so its figure
 * is a fraction of a month and is still going up.
 *
 * A green "13% of $5,500 · Aug" on the agreements list looked like a retainer
 * comfortably inside its fee. It was twelve days of August. An unfinished
 * period must never render as a settled one.
 */
export function isPartialBurn(burn: BudgetBurnLike, now = new Date()): boolean {
  if (!burn.periodMonth || burn.periodClosed) return false;
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (burn.periodMonth !== current) return false;
  return now.getDate() < new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
}

/** "1,465 of 1,000 h tracked" / "$1,207 cost of $250 fee · Aug 2026 so far" */
export function burnCaption(burn: BudgetBurnLike): string {
  const unit = (v: number) => unitOf(burn, v);
  const noun = MEASURE_NOUN[burn.measure];
  const period = burn.periodMonth
    ? ` · ${formatMonth(`${burn.periodMonth}-01`)}${isPartialBurn(burn) ? ' so far' : ''}`
    : '';
  const denom = burn.basis === 'MONTHLY_AMOUNT' ? `${unit(burn.budget)} fee` : unit(burn.budget);
  return `${unit(burn.used)} ${noun} of ${denom}${period}`;
}

/**
 * Short form for tight table cells — "of 1,000 h" / "of $250 · Aug".
 *
 * The period is NOT optional here. A monthly figure that does not say it is
 * monthly is the single most dangerous number on the agreements list.
 */
export function burnDenominator(burn: BudgetBurnLike): string {
  const base =
    burn.basis === 'HOURS'
      ? `of ${formatHours(burn.budget)} h`
      : `of ${formatMoney(burn.budget, { whole: true })}`;
  if (!burn.periodMonth) return base;
  return `${base} · ${formatMonthShort(burn.periodMonth)}${isPartialBurn(burn) ? ' so far' : ''}`;
}

/** Full sentence for the title attribute / screen readers. */
export function burnTitle(burn: BudgetBurnLike): string {
  const parts = [burnCaption(burn)];
  if (burn.periodMonth) {
    parts.push(
      burn.periodClosed
        ? 'This is the final month the agreement was live — not the current month.'
        : 'This bar covers the current month only, not the whole engagement.',
    );
    if (isPartialBurn(burn)) {
      const now = new Date();
      const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      parts.push(
        `The month is ${now.getDate()} of ${days} days in, so the hatched figure is still rising — do not read it as final.`,
      );
    }
  }
  if (burn.lifetime) {
    parts.push(
      `All time: ${unitOf(burn, burn.lifetime.used)} ${MEASURE_NOUN[burn.measure]} of ${formatMoney(
        burn.lifetime.budget,
        { whole: true },
      )} fee accrued over ${burn.lifetime.months} month${burn.lifetime.months === 1 ? '' : 's'} — ${formatPercent(burn.lifetime.pct, { decimals: 0 })}.`,
    );
  }
  return parts.join(' ');
}

/** Colour ramp shared by the period bar and the lifetime line. */
function pctTone(used: number, pct: number): string {
  // Zero is never "green" — an untouched budget is neutral, not healthy.
  if (used === 0) return 'text-grey-500';
  if (pct >= 1) return 'text-status-late';
  if (pct >= 0.75) return 'text-status-partial';
  return 'text-status-paid';
}

export interface BudgetBarProps {
  burn: BudgetBurnLike;
  /** 'compact' = table cell (short denominator), 'full' = spelled-out caption. */
  variant?: 'compact' | 'full';
  size?: 'sm' | 'md';
  className?: string;
}

export function BudgetBar({ burn, variant = 'compact', size = 'sm', className }: BudgetBarProps) {
  const full = variant === 'full';
  const caption = full ? burnCaption(burn) : burnDenominator(burn);
  const life = burn.lifetime ?? null;
  const title = burnTitle(burn);

  return (
    <div className={className} title={title}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={cn('num text-xs font-bold', pctTone(burn.used, burn.pct))}>
          {formatPercent(burn.pct, { decimals: 0 })}
        </span>
        <span className="num truncate text-xs text-grey-500">
          {caption}
          {burn.periodClosed && (
            <span className="ml-1 font-semibold text-grey-400">
              {full ? '(final month)' : 'final'}
            </span>
          )}
        </span>
      </div>
      {/* The lifetime figure is what stops a one-month bar being read as the
          agreement's health, so it is never dropped — it is deliberately on the
          SAME colour ramp as the bar, so a green month against a red life reads
          as the contradiction it is. Compact puts it on the bar's own row. */}
      <div className="mt-1 flex items-center gap-2">
        <ProgressBar
          value={burn.pct}
          size={size}
          // Hatched while the period is still running: the number is not final.
          striped={isPartialBurn(burn)}
          className="min-w-0 flex-1"
          aria-label={`Budget burn: ${title}`}
        />
        {life && !full && (
          <span
            className={cn(
              'num shrink-0 text-[0.6875rem] font-bold leading-none',
              pctTone(life.used, life.pct),
            )}
          >
            <span className="font-semibold text-grey-400">life </span>
            {formatPercent(life.pct, { decimals: 0 })}
          </span>
        )}
      </div>
      {life && full && (
        <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-dashed border-grey-200 pt-1">
          <span className={cn('num text-[0.6875rem] font-bold', pctTone(life.used, life.pct))}>
            <span className="font-semibold text-grey-400">Life </span>
            {formatPercent(life.pct, { decimals: 0 })}
          </span>
          <span className="num truncate text-[0.6875rem] text-grey-400">
            {`${unitOf(burn, life.used)} of ${formatMoney(life.budget, { whole: true })} fee over ${life.months} mo`}
          </span>
        </div>
      )}
    </div>
  );
}
